import { useEffect, useState } from "react";
import { applyAIChanges, fetchAISession, getAIErrorMessage } from "../utils/aiInterviewApi";
import {
  INTERVIEWER,
  aiMeetingReason,
  aiModeOf,
  aiSlotReason,
  interviewerChange,
  interviewerOfMode,
  interviewerOfSession,
  interviewerOptionStates,
} from "../utils/aiInterviewerChoice";

const NONE = { choice: INTERVIEWER.PANELIST, status: null };

// State for the "Interviewer" choice on the schedule and edit forms.
// Pass `interviewId` when editing so the choice starts from the interview's current AI session.
// `slots` ([{ start: Date, end: Date }]) and `meetingProvider` (see meetingProviderOf) decide whether AI can be offered.
// `aiMode` is the interview's SiftHire `ai_mode` on the edit form. The choice starts from it, while
// enable/disable is worked out against the AI session, so a change that failed on one side is redone.
const useAIInterviewSetting = (isOpen, interviewId, { slots = [], meetingProvider = null, aiMode } = {}) => {
  const savedChoice = aiMode === undefined ? undefined : interviewerOfMode(aiMode);
  const [choice, setChoice] = useState(INTERVIEWER.PANELIST);
  // The AI session's choice and status
  const [initial, setInitial] = useState(NONE);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [failures, setFailures] = useState([]);
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    setChoice(savedChoice ?? INTERVIEWER.PANELIST);
    setInitial(NONE);
    setLoadError("");
    setFailures([]);
    if (!interviewId) return undefined;

    let cancelled = false;
    setLoading(true);
    fetchAISession(interviewId)
      .then((session) => {
        if (cancelled) return;
        const current = interviewerOfSession(session);
        setInitial({ choice: current, status: session?.status || null });
        if (savedChoice === undefined) setChoice(current);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(getAIErrorMessage(err, "Couldn't load the AI interviewer status."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, interviewId, savedChoice]);

  // Only the meeting type disables the AI options, so the interviewer can be picked before the time.
  // A slot the AI can't run in is reported as a selection error and blocks saving instead.
  const blockReason = aiMeetingReason(meetingProvider);
  const current = savedChoice ?? initial.choice;
  const options = interviewerOptionStates({ initial: current, sessionStatus: initial.status, blockReason });
  const action = interviewerChange(initial.choice, choice);
  // The SiftHire side: send `aiMode` with the save; `modeChanged` when it differs from the interview's
  const modeChanged = choice !== current;
  const selectionError = action === "enable"
    ? (options[choice]?.disabled ? options[choice].reason : aiSlotReason({ slots }))
    : "";

  // Applies the choice to interviews that were just saved: [{ interviewId, label }].
  // Returns true when nothing failed; failures are kept for retry.
  const apply = async (interviews) => {
    if (!action || !interviews.length) return true;
    const failed = await applyAIChanges(interviews.map((item) => ({ ...item, action, mode: choice })));
    setFailures(failed);
    return failed.length === 0;
  };

  const retry = async () => {
    setRetrying(true);
    try {
      const failed = await applyAIChanges(failures);
      setFailures(failed);
      return failed.length === 0;
    } finally {
      setRetrying(false);
    }
  };

  return {
    choice,
    setChoice,
    aiMode: aiModeOf(choice),
    modeChanged,
    action,
    // Whether picking `next` would switch AI on for this interview (and so use a credit)
    wouldEnable: (next) => interviewerChange(initial.choice, next) === "enable",
    options,
    loading,
    loadError,
    selectionError,
    failures,
    retrying,
    apply,
    retry,
  };
};

export default useAIInterviewSetting;
